/* T113 — Light/dark theme toggle with localStorage persistence.
 *
 * The HTML root carries a `data-theme="dark"` attribute when dark
 * mode is on. Sprint-5 dark-theme CSS overrides the design tokens
 * via the `[data-theme="dark"]` selector in index.css.
 *
 * Default is "dark" per the design package's hero treatment
 * (`New UI Changes/index.html`).
 */

import { useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

/* Storage key bumped to `.v2` (2026-07-10) to reset any previously-saved
   'light' preference from testing — every browser now loads the dark
   default on next visit. The light/dark toggle still persists to this
   key as normal afterward. */
const STORAGE_KEY = 'app.theme.v2';
const DEFAULT_THEME: Theme = 'dark';

function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : DEFAULT_THEME;
}

function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  if (theme === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
}

export function useTheme(): {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
} {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    applyTheme(theme);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, theme);
    }
  }, [theme]);

  return {
    theme,
    setTheme: setThemeState,
    toggleTheme: () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')),
  };
}

/* Apply the saved theme as early as possible during app bootstrap.
 * Called once from `main.tsx` BEFORE the first render so users do
 * not see a flash of light theme on a dark-mode reload. */
export function initThemeFromStorage(): void {
  applyTheme(readStoredTheme());
}
